import React, { useState, useEffect } from 'react';
import { fetchCustomers, fetchEmployees, fetchDailyWorkOrders, fetchTasksByDate, filterTasksForActiveCustomers, fetchAllWorkOrders } from '../../Utils/api';
import './dashboard.css';
import { Box, CircularProgress, Grid, Typography, Paper, IconButton, Select, MenuItem, FormControl, Chip } from '@mui/material';
import { useAuth } from '../../Auth/AuthContext';
import { LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Icons
import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RouteIcon from '@mui/icons-material/Route';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Components
import MetricCard from '../../Components/MetricCard/MetricCard';
import QuickActionCard from '../../Components/QuickActionCard/QuickActionCard';
import ActivityFeed from '../../Components/ActivityFeed/ActivityFeed';

const Dashboard = () => {
    const [customerCount, setCustomerCount] = useState(0);
    const [inactiveCustomerCount, setInactiveCustomerCount] = useState(0);
    const [employeeCount, setEmployeeCount] = useState(0);
    const [routeStats, setRouteStats] = useState({
        todayRoutes: 0,
        weekRoutes: 0,
        todayTasks: 0,
        completedTasks: 0
    });
    const [weeklyTrendData, setWeeklyTrendData] = useState([]);
    const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // 0 = current week, -1 = last week, etc.
    const [weekDateRange, setWeekDateRange] = useState('');
    const [serviceDistribution, setServiceDistribution] = useState([]);
    const [selectedServiceWeekOffset, setSelectedServiceWeekOffset] = useState(0);
    const [serviceWeekDateRange, setServiceWeekDateRange] = useState('');
    const [recentActivities, setRecentActivities] = useState([]);
    const [overdueTasks, setOverdueTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingWeeklyData, setLoadingWeeklyData] = useState(false);
    const [loadingServiceData, setLoadingServiceData] = useState(false);
    const { companyId, userDetails } = useAuth();

    // Function to get time-based greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) {
            return "Good morning";
        } else if (hour < 18) {
            return "Good afternoon";
        } else {
            return "Good evening";
        }
    };

// Helper function to get start of week (Monday)
    const getStartOfWeek = (date) => {
        const day = date.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        const result = new Date(date);
        result.setDate(date.getDate() + diff);
        result.setHours(0, 0, 0, 0);
        return result;
    };

    // Helper function to format date range
    const formatDateRange = (startDate) => {
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        const options = { month: 'short', day: 'numeric' };
        const startStr = startDate.toLocaleDateString('en-US', options);
        const endStr = endDate.toLocaleDateString('en-US', options);

        return `${startStr} - ${endStr}`;
    };

    // Function to fetch weekly routes data
    const fetchWeeklyRoutesData = async (weekOffset = 0) => {
        if (!companyId) return;

        setLoadingWeeklyData(true);

        try {
            const today = new Date();
            const startOfWeek = getStartOfWeek(today);

            // Adjust for week offset (0 = current week, -1 = last week, etc.)
            startOfWeek.setDate(startOfWeek.getDate() + (weekOffset * 7));

            // Update date range display
            setWeekDateRange(formatDateRange(startOfWeek));

            // Fetch tasks for each day of the selected week
            const weekTasks = await Promise.all(
                Array.from({ length: 7 }, (_, i) => {
                    const date = new Date(startOfWeek);
                    date.setDate(startOfWeek.getDate() + i);
                    const dateIso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    return fetchTasksByDate(companyId, dateIso).catch(() => []);
                })
            );

            // Process weekly trend data - count unique routes (employees) per day
            const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const weeklyData = weekTasks.map((dayTasks, index) => {
                const activeTasks = Array.isArray(dayTasks) ? filterTasksForActiveCustomers(dayTasks) : [];

                // Count unique employees (routes) for this day
                const uniqueEmployees = new Set();
                activeTasks.forEach(task => {
                    if (task.employee && task.employee.employeeId) {
                        uniqueEmployees.add(task.employee.employeeId);
                    }
                });

                return {
                    day: dayNames[index],
                    routes: uniqueEmployees.size
                };
            });

            setWeeklyTrendData(weeklyData);
        } catch (error) {
            console.error('Error fetching weekly routes data:', error);
            setWeeklyTrendData([]);
        } finally {
            setLoadingWeeklyData(false);
        }
    };

    // Function to fetch overdue tasks
    const fetchOverdueTasks = async () => {
        if (!companyId) return;

        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Fetch tasks from the last 30 days to catch overdue items
            const tasksPromises = [];
            for (let i = 1; i <= 30; i++) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                const dateIso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                tasksPromises.push(fetchTasksByDate(companyId, dateIso).catch(() => []));
            }

            // Also fetch all work orders to get service type information
            const [allPastTasksResults, allWorkOrders] = await Promise.all([
                Promise.all(tasksPromises),
                fetchAllWorkOrders(companyId).catch(() => [])
            ]);

            const allPastTasks = allPastTasksResults;
            const flatTasks = allPastTasks.flat().filter(task => task && task.taskDetails);

            // Create a map of workOrderId to service type for quick lookup
            const workOrderServiceTypeMap = new Map();
            if (Array.isArray(allWorkOrders)) {
                allWorkOrders.forEach(wo => {
                    if (wo.workOrder && wo.workOrder.workOrderId) {
                        workOrderServiceTypeMap.set(
                            wo.workOrder.workOrderId,
                            wo.workOrder.workOrderServiceType || 'Regular Service'
                        );
                    }
                });
            }

            // Filter for active customers only
            const activeTasks = filterTasksForActiveCustomers(flatTasks);

            // Filter for overdue tasks (not completed, not cancelled, due date in the past)
            const overdue = activeTasks
                .filter(task => {
                    const status = task.taskDetails?.taskStatus;
                    return status !== 'Completed' && status !== 'Cancelled';
                })
                .map(task => {
                    const dueDate = new Date(task.taskDetails.workOrderTaskDueDate);
                    dueDate.setHours(0, 0, 0, 0);
                    const diffTime = today - dueDate;
                    const daysOverdue = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                    // Get service type from work order map, fallback to taskDetails, then to default
                    const workOrderId = task.taskDetails?.workOrderId;
                    const serviceType = workOrderId && workOrderServiceTypeMap.has(workOrderId)
                        ? workOrderServiceTypeMap.get(workOrderId)
                        : (task.taskDetails?.workOrderServiceType || 'Regular Service');

                    return {
                        taskId: task.taskId,
                        customerId: task.customer?.customerId || null,
                        customerName: task.customer
                            ? `${task.customer.custFirstName} ${task.customer.custLastName}`
                            : 'Unknown Customer',
                        employeeName: task.employee
                            ? `${task.employee.firstName} ${task.employee.lastName}`
                            : 'Unassigned',
                        serviceType: serviceType,
                        dueDate: task.taskDetails.workOrderTaskDueDate,
                        daysOverdue: daysOverdue,
                        status: task.taskDetails.taskStatus
                    };
                })
                .sort((a, b) => b.daysOverdue - a.daysOverdue); // Most overdue first

            // Deduplicate: keep only the most overdue task per customer
            const seen = new Set();
            const dedupedOverdue = overdue.filter(task => {
                const key = task.customerId ?? task.customerName;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            setOverdueTasks(dedupedOverdue);
        } catch (error) {
            console.error('Error fetching overdue tasks:', error);
            setOverdueTasks([]);
        }
    };

    // Function to fetch recent activities
    const fetchRecentActivities = async () => {
        if (!companyId) return;

        try {
            const activities = [];

            // Fetch all work orders, customers, and employees
            const [allWorkOrders, allCustomers, allEmployees] = await Promise.all([
                fetchAllWorkOrders(companyId).catch(() => []),
                fetchCustomers(companyId, 'Y').catch(() => []),
                fetchEmployees(companyId).catch(() => [])
            ]);

            // Process work orders
            if (Array.isArray(allWorkOrders)) {
                // Get active customer IDs for filtering
                const activeCustomerIds = new Set(allCustomers.map(c => c.customerId));

                allWorkOrders
                    .filter(wo => wo.workOrder && activeCustomerIds.has(wo.workOrder.customerId))
                    .forEach(wo => {
                        const workOrder = wo.workOrder;

                        // Find customer info
                        const customer = allCustomers.find(c => c.customerId === workOrder.customerId);
                        const customerName = customer
                            ? `${customer.custFirstName} ${customer.custLastName}`
                            : 'Unknown Customer';

                        // Find employee info
                        const employee = allEmployees.find(e => e.employeeId === workOrder.employeeId);
                        const employeeName = employee
                            ? `${employee.firstName} ${employee.lastName}`
                            : 'Unassigned';

                        // Use createdAt or initialServiceDate as timestamp
                        const timestamp = workOrder.createdAt
                            ? new Date(workOrder.createdAt)
                            : workOrder.initialServiceDate
                            ? new Date(workOrder.initialServiceDate)
                            : null;

                        const serviceType = workOrder.workOrderServiceType || 'Service';
                        const statusLabel = workOrder.statusCode === 'Cancelled' ? '(Cancelled)' : '';

                        activities.push({
                            id: `work_order_${workOrder.workOrderId}`,
                            type: 'work_order_created',
                            title: 'Work order created',
                            description: `${serviceType} for ${customerName} - assigned to ${employeeName} ${statusLabel}`.trim(),
                            timestamp: timestamp
                        });
                    });
            }

            // Process recently added customers
            // Since customers don't have createdAt, show the 5 most recent by customerId
            if (Array.isArray(allCustomers) && allCustomers.length > 0) {
                const sortedCustomers = [...allCustomers].sort((a, b) => b.customerId - a.customerId);
                const recentCustomers = sortedCustomers.slice(0, 5);

                recentCustomers.forEach(customer => {
                    const timestamp = customer.creaTs ? new Date(customer.creaTs) : null;

                    // Note: Backend doesn't track who added customers, showing generic admin
                    const adminName = userDetails?.name || 'Admin';

                    activities.push({
                        id: `customer_added_${customer.customerId}`,
                        type: 'customer_added',
                        title: 'New customer added',
                        description: `${customer.custFirstName} ${customer.custLastName} from ${customer.custCity}, ${customer.custState} - Added by ${adminName}`,
                        timestamp: timestamp
                    });
                });
            }

            // Process recently added employees
            // Since employees don't have createdAt, show the 5 most recent by employeeId
            if (Array.isArray(allEmployees) && allEmployees.length > 0) {
                const sortedEmployees = [...allEmployees].sort((a, b) => b.employeeId - a.employeeId);
                const recentEmployees = sortedEmployees.slice(0, 5);

                recentEmployees.forEach(employee => {
                    const timestamp = employee.createdAt ? new Date(employee.createdAt) : null;

                    // Note: Backend doesn't track who added employees, showing generic admin
                    const adminName = userDetails?.name || 'Admin';

                    activities.push({
                        id: `employee_added_${employee.employeeId}`,
                        type: 'employee_added',
                        title: 'New employee added',
                        description: `${employee.firstName} ${employee.lastName} joined as ${employee.empDesignation || 'team member'} - Added by ${adminName}`,
                        timestamp: timestamp
                    });
                });
            }

            // Sort by timestamp (most recent first) and limit to 10; null timestamps go to the end
            const sortedActivities = activities
                .sort((a, b) => {
                    if (!a.timestamp && !b.timestamp) return 0;
                    if (!a.timestamp) return 1;
                    if (!b.timestamp) return -1;
                    return b.timestamp - a.timestamp;
                })
                .slice(0, 10);

            setRecentActivities(sortedActivities);
        } catch (error) {
            console.error('Error fetching recent activities:', error);
            setRecentActivities([]);
        }
    };

    // Function to fetch service distribution data for a specific week
    const fetchServiceDistributionData = async (weekOffset = 0) => {
        if (!companyId) return;

        setLoadingServiceData(true);

        try {
            const today = new Date();
            const startOfWeek = getStartOfWeek(today);

            // Adjust for week offset
            startOfWeek.setDate(startOfWeek.getDate() + (weekOffset * 7));
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            // Update date range display
            setServiceWeekDateRange(formatDateRange(startOfWeek));

            // Fetch all work orders and active customers
            const [allWorkOrders, activeData] = await Promise.all([
                fetchAllWorkOrders(companyId).catch(() => []),
                fetchCustomers(companyId, 'Y').catch(() => [])
            ]);

            if (Array.isArray(allWorkOrders) && allWorkOrders.length > 0) {
                // Get active customer IDs
                const activeCustomerIds = new Set(activeData.map(c => c.customerId));

                // Filter work orders for active customers and within the selected week
                const activeWorkOrders = allWorkOrders.filter(wo => {
                    if (!wo.workOrder || wo.workOrder.statusCode === 'Cancelled') return false;
                    if (!activeCustomerIds.has(wo.workOrder.customerId)) return false;

                    // Check if work order is within the selected week
                    if (wo.workOrder.initialServiceDate) {
                        const serviceDate = new Date(wo.workOrder.initialServiceDate);
                        return serviceDate >= startOfWeek && serviceDate <= endOfWeek;
                    }

                    return true; // Include if no date filtering is needed
                });

                // Count service types
                const serviceCounts = {
                    'Regular Service': 0,
                    'Back-Wash': 0,
                    'Filter Breakdown': 0,
                    'On Demand': 0
                };

                activeWorkOrders.forEach(wo => {
                    const serviceType = wo.workOrder?.workOrderServiceType;

                    // Normalize service type to match our keys
                    if (!serviceType) return;

                    if (serviceType === 'Regular Service') {
                        serviceCounts['Regular Service']++;
                    } else if (serviceType === 'Back-wash' || serviceType === 'Back-Wash') {
                        serviceCounts['Back-Wash']++;
                    } else if (serviceType === 'Filter breakdown' || serviceType === 'Filter Breakdown') {
                        serviceCounts['Filter Breakdown']++;
                    } else if (serviceType === 'On Demand') {
                        serviceCounts['On Demand']++;
                    } else {
                        // Log unrecognized service types for debugging
                        console.log('Unrecognized service type:', serviceType);
                    }
                });

                // Calculate total for percentages
                const total = Object.values(serviceCounts).reduce((sum, count) => sum + count, 0);

                // Convert to distribution array with colors - always show all service types
                const distribution = [
                    {
                        name: 'Regular Service',
                        value: serviceCounts['Regular Service'],
                        percentage: total > 0 ? Math.round((serviceCounts['Regular Service'] / total) * 100) : 0,
                        color: '#3B82F6'
                    },
                    {
                        name: 'Back-Wash',
                        value: serviceCounts['Back-Wash'],
                        percentage: total > 0 ? Math.round((serviceCounts['Back-Wash'] / total) * 100) : 0,
                        color: '#10B981'
                    },
                    {
                        name: 'Filter Breakdown',
                        value: serviceCounts['Filter Breakdown'],
                        percentage: total > 0 ? Math.round((serviceCounts['Filter Breakdown'] / total) * 100) : 0,
                        color: '#F59E0B'
                    },
                    {
                        name: 'On Demand',
                        value: serviceCounts['On Demand'],
                        percentage: total > 0 ? Math.round((serviceCounts['On Demand'] / total) * 100) : 0,
                        color: '#EF4444'
                    }
                ];

                setServiceDistribution(distribution);
            } else {
                // Show all service types with 0 values when no data
                setServiceDistribution([
                    { name: 'Regular Service', value: 0, percentage: 0, color: '#3B82F6' },
                    { name: 'Back-Wash', value: 0, percentage: 0, color: '#10B981' },
                    { name: 'Filter Breakdown', value: 0, percentage: 0, color: '#F59E0B' },
                    { name: 'On Demand', value: 0, percentage: 0, color: '#EF4444' }
                ]);
            }
        } catch (error) {
            console.error('Error fetching service distribution data:', error);
            // Show all service types with 0 values on error
            setServiceDistribution([
                { name: 'Regular Service', value: 0, percentage: 0, color: '#3B82F6' },
                { name: 'Back-Wash', value: 0, percentage: 0, color: '#10B981' },
                { name: 'Filter Breakdown', value: 0, percentage: 0, color: '#F59E0B' },
                { name: 'On Demand', value: 0, percentage: 0, color: '#EF4444' }
            ]);
        } finally {
            setLoadingServiceData(false);
        }
    };

    // Fetch all dashboard data when companyId is available
    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!companyId) {
                console.log('No companyId available, waiting for authentication');
                return;
            }

            setLoading(true);

            try {
                // Get today's date in ISO format
                const today = new Date();
                const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                // Fetch all data in parallel
                const [activeData, inactiveData, employeeData, todayTasks] = await Promise.all([
                    fetchCustomers(companyId, 'Y'),
                    fetchCustomers(companyId, 'N'),
                    fetchEmployees(companyId),
                    fetchTasksByDate(companyId, todayIso).catch(() => [])
                ]);

                // Validate responses and ensure they're arrays
                const activeCount = Array.isArray(activeData) ? activeData.length : 0;
                const inactiveCount = Array.isArray(inactiveData) ? inactiveData.length : 0;
                const empCount = Array.isArray(employeeData) ? employeeData.length : 0;

                setCustomerCount(activeCount);
                setInactiveCustomerCount(inactiveCount);
                setEmployeeCount(empCount);

                // Calculate route stats from today's tasks
                if (Array.isArray(todayTasks) && todayTasks.length > 0) {
                    // Filter for active customers
                    const activeTasks = filterTasksForActiveCustomers(todayTasks);

                    // Count unique employees (routes)
                    const uniqueEmployees = new Set();
                    let completedCount = 0;

                    activeTasks.forEach(task => {
                        if (task.employee && task.employee.employeeId) {
                            uniqueEmployees.add(task.employee.employeeId);
                        }
                        if (task.taskDetails && task.taskDetails.taskStatus === 'Completed') {
                            completedCount++;
                        }
                    });

                    setRouteStats({
                        todayRoutes: uniqueEmployees.size,
                        weekRoutes: uniqueEmployees.size,
                        todayTasks: activeTasks.length,
                        completedTasks: completedCount
                    });
                } else {
                    setRouteStats({
                        todayRoutes: 0,
                        weekRoutes: 0,
                        todayTasks: 0,
                        completedTasks: 0
                    });
                }

                // Fetch initial weekly data (current week)
                fetchWeeklyRoutesData(0);

                // Fetch initial service distribution (current week)
                fetchServiceDistributionData(0);

                // Fetch recent activities
                fetchRecentActivities();

                // Fetch overdue tasks
                fetchOverdueTasks();

            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                // Don't reset data to 0 on error - keep previous values
                // User will be redirected to login if it's an auth error (handled by axios interceptor)
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [companyId]);

    // Fetch weekly data when week offset changes
    useEffect(() => {
        if (companyId) {
            fetchWeeklyRoutesData(selectedWeekOffset);
        }
    }, [selectedWeekOffset, companyId]);

    // Fetch service distribution when week offset changes
    useEffect(() => {
        if (companyId) {
            fetchServiceDistributionData(selectedServiceWeekOffset);
        }
    }, [selectedServiceWeekOffset, companyId]);

    // Refresh activities when dashboard becomes visible or periodically
    useEffect(() => {
        if (!companyId) return;

        // Refresh activities when page becomes visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchRecentActivities();
                fetchOverdueTasks();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Also refresh activities and overdue tasks every 30 seconds
        const intervalId = setInterval(() => {
            fetchRecentActivities();
            fetchOverdueTasks();
        }, 30000); // 30 seconds

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(intervalId);
        };
    }, [companyId]);

    // Refresh dashboard data every 5 minutes to prevent stale data
    useEffect(() => {
        if (!companyId) return;

        const refreshInterval = setInterval(async () => {
            console.log('Refreshing dashboard data...');
            // Trigger a re-fetch by calling the main fetch function
            const today = new Date();
            const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            try {
                const [activeData, inactiveData, employeeData, todayTasks] = await Promise.all([
                    fetchCustomers(companyId, 'Y'),
                    fetchCustomers(companyId, 'N'),
                    fetchEmployees(companyId),
                    fetchTasksByDate(companyId, todayIso).catch(() => [])
                ]);

                const activeCount = Array.isArray(activeData) ? activeData.length : 0;
                const inactiveCount = Array.isArray(inactiveData) ? inactiveData.length : 0;
                const empCount = Array.isArray(employeeData) ? employeeData.length : 0;

                setCustomerCount(activeCount);
                setInactiveCustomerCount(inactiveCount);
                setEmployeeCount(empCount);

                // Calculate route stats from today's tasks
                if (Array.isArray(todayTasks) && todayTasks.length > 0) {
                    const activeTasks = filterTasksForActiveCustomers(todayTasks);
                    const uniqueEmployees = new Set();
                    let completedCount = 0;

                    activeTasks.forEach(task => {
                        if (task.employee && task.employee.employeeId) {
                            uniqueEmployees.add(task.employee.employeeId);
                        }
                        if (task.taskDetails && task.taskDetails.taskStatus === 'Completed') {
                            completedCount++;
                        }
                    });

                    setRouteStats({
                        todayRoutes: uniqueEmployees.size,
                        weekRoutes: uniqueEmployees.size,
                        todayTasks: activeTasks.length,
                        completedTasks: completedCount
                    });
                }

                console.log('Dashboard data refreshed successfully', {
                    activeCustomers: activeCount,
                    inactiveCustomers: inactiveCount,
                    employees: empCount
                });
            } catch (error) {
                console.error('Error refreshing dashboard data:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.status,
                    data: error.response?.data
                });
                // If we get an authentication error, the user should be redirected to login
                // This is handled by the API interceptor
            }
        }, 5 * 60 * 1000); // Refresh every 5 minutes

        return () => clearInterval(refreshInterval);
    }, [companyId]);

    // for fetching all the details of workOrders

    // useEffect(()=>{
    //     const fetchWorkOrders=async()=>{
    //         setLoading(true);

    //         try{
    //             const today=await fetchDailyWorkOrders(companyId,"today");
    //             const week=await fetchDailyWorkOrders(companyId,"week");
    //             const month=await fetchDailyWorkOrders(companyId,"month");
    //             const total=await fetchDailyWorkOrders(companyId,"total");
    //             console.log(today.length);
    //             setWorkOrders({today,week,month,total});
    //         }
    //         catch(error){
    //             console.error('Error fetching work orders:',error);
    //         }finally{
    //             setLoading(false);
    //         }
    //     };
    //     fetchWorkOrders();
    // }, []);

    return (
        <div className="main-content">
            <Box sx={{ maxWidth: '1600px', width: '100%', margin: '0 auto', padding: '20px' }}>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            color: '#002244',
                            fontWeight: 700,
                            fontSize: '2rem',
                            mb: 0.5
                        }}
                    >
                        {getGreeting()}, {userDetails?.name || 'User'}!
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1rem'
                        }}
                    >
                        Here's what's happening with your pool service business today
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                ) : (
                    <>
                        {/* Metrics Cards */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={4}>
                                <MetricCard
                                    title="Active Customers"
                                    value={customerCount}
                                    icon={<GroupIcon />}
                                    color="#3B82F6"
                                    subtitle={`${inactiveCustomerCount} inactive`}
                                    trend="up"
                                    trendValue="+12%"
                                    link="/view-customer"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <MetricCard
                                    title="Team Members"
                                    value={employeeCount}
                                    icon={<BadgeIcon />}
                                    color="#10B981"
                                    subtitle="Total employees"
                                    link="/view-emp"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <MetricCard
                                    title="Today's Routes"
                                    value={routeStats.todayRoutes}
                                    icon={<RouteIcon />}
                                    color="#F59E0B"
                                    subtitle={`${routeStats.todayTasks} tasks scheduled`}
                                    trend="up"
                                    trendValue="+8%"
                                    link="/routes-option-1"
                                />
                            </Grid>
                        </Grid>

                        {/* Quick Actions */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#002244',
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    mb: 2
                                }}
                            >
                                Quick Actions
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <QuickActionCard
                                        title="Add Customer"
                                        description="Register new customer"
                                        icon={<AddIcon />}
                                        color="#3B82F6"
                                        link="/add-customer"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <QuickActionCard
                                        title="Add Employee"
                                        description="Register new employee"
                                        icon={<PersonAddIcon />}
                                        color="#10B981"
                                        link="/add-emp"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <QuickActionCard
                                        title="Schedule Service"
                                        description="Plan employee schedules"
                                        icon={<ScheduleIcon />}
                                        color="#F59E0B"
                                        link="/employee-scheduling"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <QuickActionCard
                                        title="View Routes"
                                        description="Manage service routes"
                                        icon={<RouteIcon />}
                                        color="#EF4444"
                                        link="/routes-option-1"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <QuickActionCard
                                        title="Reports"
                                        description="Analytics & insights"
                                        icon={<AssessmentIcon />}
                                        color="#8B5CF6"
                                        link="/reports"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Charts and Activity Feed */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            {/* Weekly Trend Chart */}
                            <Grid item xs={12} md={8}>
                                <Paper
                                    sx={{
                                        borderRadius: '16px',
                                        border: '2px solid #e0e0e0',
                                        padding: 3,
                                        boxShadow: 'none'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#002244',
                                                    fontWeight: 600,
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                Routes Weekly Snapshot
                                            </Typography>
                                            {weekDateRange && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#666',
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    {weekDateRange}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                onClick={() => setSelectedWeekOffset(prev => prev - 1)}
                                                size="small"
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#002244',
                                                    fontWeight: 600,
                                                    minWidth: '100px',
                                                    textAlign: 'center',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                {selectedWeekOffset === 0 ? 'This Week' :
                                                 selectedWeekOffset === -1 ? 'Last Week' :
                                                 `${Math.abs(selectedWeekOffset)} Weeks Ago`}
                                            </Typography>
                                            <IconButton
                                                onClick={() => setSelectedWeekOffset(prev => prev + 1)}
                                                disabled={selectedWeekOffset >= 0}
                                                size="small"
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                                    '&:disabled': { opacity: 0.3 }
                                                }}
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {loadingWeeklyData ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                            <CircularProgress size={40} />
                                        </Box>
                                    ) : weeklyTrendData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <AreaChart data={weeklyTrendData}>
                                                <defs>
                                                    <linearGradient id="colorRoutes" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                                <XAxis dataKey="day" stroke="#666" />
                                                <YAxis stroke="#666" allowDecimals={false} />
                                                <Tooltip
                                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                                                    labelStyle={{ color: '#002244', fontWeight: 600 }}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="routes"
                                                    stroke="#3B82F6"
                                                    strokeWidth={3}
                                                    fillOpacity={1}
                                                    fill="url(#colorRoutes)"
                                                    name="Routes"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                No route data available for this week
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>

                            {/* Service Distribution Pie Chart */}
                            <Grid item xs={12} md={4}>
                                <Paper
                                    sx={{
                                        borderRadius: '16px',
                                        border: '2px solid #e0e0e0',
                                        padding: 3,
                                        boxShadow: 'none',
                                        height: '100%'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#002244',
                                                    fontWeight: 600,
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                Service Distribution
                                            </Typography>
                                            {serviceWeekDateRange && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#666',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    {serviceWeekDateRange}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton
                                                onClick={() => setSelectedServiceWeekOffset(prev => prev - 1)}
                                                size="small"
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    width: 28,
                                                    height: 28,
                                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                                }}
                                            >
                                                <ChevronLeftIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => setSelectedServiceWeekOffset(prev => prev + 1)}
                                                disabled={selectedServiceWeekOffset >= 0}
                                                size="small"
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    width: 28,
                                                    height: 28,
                                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                                    '&:disabled': { opacity: 0.3 }
                                                }}
                                            >
                                                <ChevronRightIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {loadingServiceData ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                            <CircularProgress size={40} />
                                        </Box>
                                    ) : (
                                        <>
                                            {serviceDistribution.some(item => item.value > 0) ? (
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <PieChart>
                                                        <Pie
                                                            data={serviceDistribution.filter(item => item.value > 0)}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={60}
                                                            outerRadius={90}
                                                            paddingAngle={5}
                                                            dataKey="value"
                                                        >
                                                            {serviceDistribution.filter(item => item.value > 0).map((entry) => (
                                                                <Cell key={`cell-${entry.name}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip
                                                            formatter={(value, name, props) => [`${value} work orders (${props.payload.percentage}%)`, props.payload.name]}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                                                    <Typography variant="body2" sx={{ color: '#999' }}>
                                                        No service data for this week
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ mt: 2 }}>
                                                {serviceDistribution.map((item) => (
                                                    <Box
                                                        key={item.name}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            mb: 1
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                sx={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: item.color,
                                                                    mr: 1
                                                                }}
                                                            />
                                                            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                                                                {item.name}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#002244' }}>
                                                            {item.value} ({item.percentage}%)
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Activity Feed */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <ActivityFeed activities={recentActivities} onRefresh={fetchRecentActivities} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper
                                    sx={{
                                        borderRadius: '16px',
                                        border: '2px solid #e0e0e0',
                                        padding: 3,
                                        boxShadow: 'none',
                                        height: '100%'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: '#002244',
                                                fontWeight: 600,
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            Overdue Tasks (previous 30 days)
                                        </Typography>
                                        <Chip
                                            label={`${overdueTasks.length} ${overdueTasks.length === 1 ? 'task' : 'tasks'}`}
                                            size="small"
                                            sx={{
                                                backgroundColor: overdueTasks.length > 0 ? '#FEE2E2' : '#E8F4F8',
                                                color: overdueTasks.length > 0 ? '#EF4444' : '#002244',
                                                fontWeight: 600,
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </Box>
                                    {overdueTasks.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 1112, overflowY: 'auto' }}>
                                            {overdueTasks.map((task, index) => (
                                                <Box
                                                    key={task.taskId}
                                                    sx={{
                                                        padding: '12px',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#FEF2F2',
                                                        border: '1px solid #FEE2E2',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: '#FEE2E2',
                                                            transform: 'translateX(4px)'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#002244',
                                                                fontWeight: 600,
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            {task.customerName}
                                                        </Typography>
                                                        <Chip
                                                            label={`${task.daysOverdue} ${task.daysOverdue === 1 ? 'day' : 'days'} overdue`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: '#EF4444',
                                                                color: 'white',
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                height: '20px'
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            label={task.serviceType}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: task.serviceType === 'Regular Service' ? '#3B82F6' :
                                                                                  task.serviceType === 'Back-wash' || task.serviceType === 'Back-Wash' ? '#10B981' :
                                                                                  task.serviceType === 'Filter breakdown' || task.serviceType === 'Filter Breakdown' ? '#F59E0B' :
                                                                                  task.serviceType === 'On Demand' ? '#EF4444' : '#6B7280',
                                                                color: 'white',
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                height: '18px'
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: '#666',
                                                                fontSize: '0.8rem'
                                                            }}
                                                        >
                                                            Assigned to {task.employeeName}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#999',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        Due: {new Date(task.dueDate).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                                            <CheckCircleIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
                                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '1.1rem', color: '#10B981' }}>
                                                All caught up!
                                            </Typography>
                                            <Typography variant="body2">
                                                No overdue tasks at the moment
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Box>
        </div>
    );
};

export default Dashboard;
